import React from 'react'
import { themeGet } from '@styled-system/theme-get'
import styled, { keyframes, css } from 'styled-components/macro'
import {
  border,
  BorderProps,
  color,
  ColorProps,
  compose,
  flexbox,
  FlexboxProps,
  grid,
  GridProps,
  GridTemplateColumnsProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  textStyle,
} from 'styled-system'
import palette from 'src/designSystem/variables/palette'

export type ReusableGridProps = LayoutProps &
  GridProps &
  FlexboxProps &
  SpaceProps & {
    show?: boolean
    gridTemplateColumns: GridTemplateColumnsProps
  }

export const Grid = styled.div.attrs<ReusableGridProps>((props) => ({
  className: 'grid',
  show: true,
  ...props,
}))<ReusableGridProps>`
  ${compose(layout, grid, flexbox, space)}
  display: ${(props) => (props.show ? 'grid' : 'none')};
  position: relative;
`

const aiGlowAnimation = keyframes`
  0% {
    background-color: ${palette.neutral['30']}
  }
  16% {
    background-color: ${palette.purple['100']};
  }
  33% {
    background-color: ${palette.purple['30']};
  }
  50% {
    background-color: ${palette.purple['100']};
  }
  66% {
    background-color: ${palette.purple['30']};
  }
  83% {
    background-color: ${palette.purple['50']};
  }
  100% {
    background-color: ${palette.neutral['30']}
  }
`

export const GridContainer = styled.div.attrs({
  className: 'grid-container',
})<{ $withTopPadding?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ $withTopPadding }) => ($withTopPadding ? '16px 0' : '0 24px')};
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  height: 100%;
  width: 100%;
`

export const GridRow = styled.div.attrs({
  'data-grid-row': '',
})<{
  selected?: boolean
  $aiglow?: boolean
  $isDisabled?: boolean
  $indexingFailed?: boolean
  $overrideColumnColor?: boolean
}>`
  display: contents;

  > a:not(:hover) {
    text-decoration: none;
  }

  > .grid-column {
    border-top: ${(props) => (props.selected ? '1px solid green' : '')};
    border-bottom: ${(props) => (props.selected ? '1px solid green' : '')};
  }

  .grid-column:first-child {
    border-left: ${(props) => (props.selected ? '1px solid green' : '')};
  }
  .grid-column:last-child {
    border-right: ${(props) => (props.selected ? '1px solid green' : '')};
  }
  &:hover {
    > a {
      ${themeGet('colorStyles.link.primary.:hover')}
      text-decoration: underline;
    }

    > .accordion-collapse:hover ~ a {
      color: unset;
      text-decoration: none;
    }

    ${({ $overrideColumnColor }) =>
      !$overrideColumnColor &&
      css`
        > .grid-column {
          background-color: ${themeGet('colors.bg.neutralHover')};
        }
      `}
  }

  > .grid-column {
    ${({ $aiglow }) =>
      $aiglow &&
      css`
        animation: ${aiGlowAnimation} 9s ease-in-out;
      `}
  }
  .grid-header {
    top: 0;
    position: sticky;
    z-index: 1;
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    background-color: ${palette.neutral['100']};
    cursor: default;
    &:hover {
      .grid-column {
        background-color: ${palette.neutral['100']};
      }
    }
    .grid-column {
      background-color: ${palette.neutral['100']};
      pointer-events: none;
      
      button, a, .dropdown-menu, .dropdown-item {
        pointer-events: auto;
      }
    }
  `}

  ${({ $indexingFailed, $isDisabled }) =>
    $indexingFailed &&
    !$isDisabled &&
    `
    background-color: ${palette.neutral['100']};
    &:hover {
      .grid-column {
        background-color: ${palette.neutral['100']};
      }
    }
    .grid-column {
      background-color: ${palette.neutral['100']};
    }
  `}
`

export type GridItemProps = GridProps &
  FlexboxProps &
  LayoutProps &
  PositionProps &
  SpaceProps &
  BorderProps &
  ColorProps

const GridItem = styled.div.attrs<GridItemProps>((props) => ({
  alignItems: props.alignItems || 'center',
  style: { gridRow: props.gridRow, ...props.style },
}))<GridItemProps>`
  display: flex;
  position: relative;
  text-align: left;
  justify-content: flex-start;
  ${({ gridRow: _unused, ...propsWithoutGridRow }) =>
    compose(
      grid,
      flexbox,
      layout,
      position,
      space,
      border,
      color,
      textStyle,
    )(propsWithoutGridRow)};
`

export const GridHeader = styled(GridItem).attrs<GridItemProps>((props) => ({
  className: 'grid-header',
  pt: 's',
  pb: 'xs',
  px: 'm',
  bg: 'white',
  color: 'text.med',
  textStyle: 'subtitle.3',
  ...props,
}))``

export const GridColumn = styled(GridItem).attrs<GridItemProps>((props) => ({
  className: 'grid-column',
  border: 'none',
  mb: 'xxs',
  px: 'm',
  py: 'xs',
  bg: 'bg.neutral',
  color: 'text.default',
  textStyle: 'body.default',
  minHeight: '48px',
  ...props,
}))`
  &:first-child,
  &:first-child:before {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  &:last-child,
  &:last-child:before {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`

export const ActionGridColumn = styled(GridColumn)`
  justify-content: right;
`

export const EmptyGrid = ({ message }: { message: string }): JSX.Element => (
  <GridRow>
    <GridColumn
      gridColumn="1 / -1"
      justifyContent="center"
      style={{ cursor: 'default' }}
    >
      {message}
    </GridColumn>
  </GridRow>
)

export type VirtualGridProps = {
  headers: JSX.Element
  items: JSX.Element[]
  itemHeight: number
  gridTemplateColumns: string
  height?: number
  width?: number
}

export const VirtualGrid = ({
  headers,
  items,
  itemHeight,
  gridTemplateColumns,
  height,
  width,
}: VirtualGridProps): JSX.Element => {
  const [scrollTop, setScrollTop] = React.useState(0)
  const [windowHeight, setWindowHeight] = React.useState(0)
  const listRef = React.useRef<HTMLDivElement>(null)

  const visibleItemCount = Math.ceil(windowHeight / itemHeight)
  const totalHeight = items.length * itemHeight

  const startIndex = Math.floor(scrollTop / itemHeight)
  const endIndex = Math.min(startIndex + visibleItemCount + 1, items.length)

  const onScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop)
  }, [])

  React.useEffect(() => {
    const listElement = listRef.current
    if (listElement) {
      const updateHeight = () => {
        if (!height) setWindowHeight(listElement.clientHeight)
        else setWindowHeight(height)
      }
      updateHeight()
      window.addEventListener('resize', updateHeight)
      listElement.addEventListener(
        'scroll',
        onScroll as unknown as EventListener,
      )
      return () => {
        window.removeEventListener('resize', updateHeight)
        listElement.removeEventListener(
          'scroll',
          onScroll as unknown as EventListener,
        )
      }
    }
  }, [onScroll, height])

  const displayItems = React.useMemo(() => {
    const visibleItems = []
    for (let i = startIndex; i < endIndex; i++) {
      const item = items[i]
      const itemStyle = {
        display: 'grid',
        gridTemplateColumns: gridTemplateColumns,
        position: 'absolute',
        top: `${i * itemHeight}px`,
        height: `${itemHeight}px`,
        left: 0,
        right: 0,
      }
      visibleItems.push(
        React.cloneElement(item, {
          key: i,
          style: { ...item.props.style, ...itemStyle },
        }),
      )
    }
    return visibleItems
  }, [startIndex, endIndex, items, gridTemplateColumns, itemHeight])

  return (
    <>
      <Grid
        gridTemplateColumns={gridTemplateColumns}
        width={width || '100%'}
        height={itemHeight}
      >
        {headers}
      </Grid>
      <div
        role="list"
        style={{
          overflowY: 'auto',
          position: 'relative',
          width: width || '100%',
          height: height || '100%',
        }}
        ref={listRef}
      >
        <Grid
          gridTemplateColumns={gridTemplateColumns}
          height={`${totalHeight}px`}
        >
          {displayItems.map((item, index) => (
            <div role="listitem" key={index}>
              {item}
            </div>
          ))}
        </Grid>
      </div>
    </>
  )
}
